import {
  Alert,
  Box,
  Button,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import "./Checkout.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Loader } from "../loader/Loader";
import CartProductCard from "./common/CartProductCard";
import RenderSvg from "../renderSvg/RenderSvg";
import { Divider } from "@mantine/core";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import { getMetaDataConfig } from "../../services/uiConfigService";
import { ConfirmationPopUp } from "../popup/confirmationPopUp";
import CartSummaryFooter from "../dms_common/CartSummaryFooter";
import { styled } from "@mui/system";
import CustomDataGrid from "../dms_common/CustomDataGrid";
import { presellerReviewColumnsMetaData } from "./pre-seller/PresellerUtils";
import { loadoutsSalesData, Product } from "./common/types";
import { getValidateRequestBody } from "./common/CreateLoadoutService";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import {
  fetchLoadoutConfigs,
  printLoadslip,
} from "./pre-seller/PresellerLoadoutService";
import {
  createLoadout,
  createLoadoutOrchestrator,
  updateLoadout,
} from "../../services/loadoutService";
import { getDataFromLocalDb } from "../../services/dbService";
import { useDispatch } from "react-redux";
import { GenericPopUp } from "../popup/genericPopUp";
import { popupType } from "../../types/types";
import CustomGenericModal from "../dms_common/CustomGenericModal";
import { getUomLabel } from "../dms_common/DmsUtils";
import { logLoadTrace } from "../../firebase";
import { errorCodeToMessageMap } from "./pre-seller/PresellerUtils";

const CustomToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  borderRadius: "8px",
  height: "100%",
  padding: "1px",
  "& .MuiToggleButton-root": {
    border: "none",
    borderRadius: "8px",
    color: "#6B6B6B",
    fontWeight: 500,
    textTransform: "none",
    backgroundColor: "#FFFFFF",
    "&:hover": {
      backgroundColor: "#F0FBFF",
      color: "rgb(93, 195, 177)",
    },
    "&.Mui-selected": {
      backgroundColor: "#F0FBFF",
      color: "rgb(93, 195, 177)",
      fontWeight: 600,
    },
    "&.Mui-selected:hover": {
      backgroundColor: "#F0FBFF",
      color: "rgb(93, 195, 177)",
    },
  },
}));

interface CheckoutProps {
  displaySummary: boolean;
}

const Checkout = ({ displaySummary }: CheckoutProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const uomLabels = getUomLabel();

  const location: any = useLocation();
  const viewLoadout =
    location?.state?.loadoutStatus === "IN-TRANSIT" ||
    location?.state?.loadoutStatus === "DELIVERED";

  const { loadNumber } = useParams();
  const cart = useSelector((state: any) => state.presellerLoadoutState);
  const [totalUniqueOutlets, setTotalUniqueOutlets] = useState<number>(0);
  const [confirmPopupState, setConfirmPopupState] = useState<boolean>(false);
  const [configObj, setConfigObj] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [toggleBtn, setToggleBtn] = useState("sku");
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [showLoadslipPopup, setShowLoadslipPopup] = useState<boolean>(false);
  const [openGenericModal, setOpenGenericModal] = useState<boolean>(false);
  const [popupType, setPopupType] = useState<popupType>("Alert");
  const [genericMessage, setGenericMessage] = useState<string>("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const formattedAmount = new Intl.NumberFormat("en-IN").format(
    Number(cart?.totals?.totalAmount)
  );
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [loadoutNumber, setLoadoutNumber] = useState<string>("");
  const [selectedCheckboxes, setSelectedCheckboxes] = useState<number[]>([]);
  const [errorMessageText, setErrorMessageText] = useState<string>("");
  const [filtersLabel, setFiltersLabel] = useState<string>("");

  const summaryValues = {
    salesman: cart?.cartData?.loadout?.SalesMan || "-",
    salesRepOrRoute: (() => {
      const uniqueSalesReps = Array.from(
        new Set(
          cart?.cartData?.salesData?.map(
            (row: loadoutsSalesData) => `${row.salesRepId}-${row.salesRepName}`
          )
        )
      );
      return (
        <>
          {uniqueSalesReps.length > 2 ? (
            <>
              {uniqueSalesReps.slice(0, 2).join(", ")}{" "}
              <Tooltip title={uniqueSalesReps.join(", ")} placement="top" arrow>
                <span
                  style={{
                    cursor: "pointer",
                    textDecoration: "underline",
                    color: "#1D8DED",
                  }}
                >
                  & {uniqueSalesReps.length - 2} more
                </span>
              </Tooltip>
            </>
          ) : (
            uniqueSalesReps.join(", ") || "-"
          )}
        </>
      );
    })(),
    deliveryRoute: (() => {
      const uniqueDeliveryRoutes = Array.from(
        new Set(
          cart?.cartData?.salesData
            ?.map((row: loadoutsSalesData) => {
              const routeId = row.salesRoute;
              const selectedRoutes =
                cart?.cartData?.loadout?.selectedRoute || [];
              const matchingSelectedRoute = selectedRoutes.find(
                (selectedRoute: string) => {
                  const [selectedRouteId] = selectedRoute.split(" - ");
                  return selectedRouteId === routeId;
                }
              );
              return matchingSelectedRoute || null;
            })
            .filter((route: string | null): route is string => route !== null)
        )
      );
      return (
        <>
          {uniqueDeliveryRoutes.length > 2 ? (
            <>
              {uniqueDeliveryRoutes.slice(0, 2).join(", ")}{" "}
              <Tooltip
                title={uniqueDeliveryRoutes.join(", ")}
                placement="top"
                arrow
              >
                <span
                  style={{
                    cursor: "pointer",
                    textDecoration: "underline",
                    color: "#1D8DED",
                  }}
                >
                  & {uniqueDeliveryRoutes.length - 2} more
                </span>
              </Tooltip>
            </>
          ) : (
            uniqueDeliveryRoutes.join(", ") || "-"
          )}
        </>
      );
    })(),
    vehicle: cart?.cartData?.loadout?.Vehicle?.vehicleType || "-",
    vehicleNo: cart?.cartData?.loadout?.Vehicle?.vehicleNumber || "-",
    totalOutlets: totalUniqueOutlets || "-",
    [configObj?.loadoutConfigs?.dateFilter
      ? configObj?.loadoutConfigs?.dateFilter
      : "deliveryDate"]:
      cart?.cartData?.loadout?.deliveryStartDate &&
      cart?.cartData?.loadout?.deliveryEndDate
        ? `${dayjs(cart?.cartData?.loadout?.deliveryStartDate.$d).format(
            "DD/MM/YY"
          )} - ${dayjs(cart?.cartData?.loadout?.deliveryEndDate.$d).format(
            "DD/MM/YY"
          )}`
        : "-",
    totalProducts: Object.keys(cart?.cartData?.product || {}).length || "-",
    totalQty:
      [
        configObj?.caseUnitLabel && cart?.totals?.totalCase
          ? `${cart.totals.totalCase} ${configObj.caseUnitLabel}`
          : null,
        configObj?.otherUnitLabel && cart?.totals?.totalOther
          ? `${cart.totals.totalOther} ${configObj.otherUnitLabel}`
          : null,
        configObj?.packUnitLabel && cart?.totals?.totalPiece
          ? `${cart.totals.totalPiece} ${configObj.packUnitLabel}`
          : null,
      ]
        .filter(Boolean)
        .join(", ") || "-",

    totalAmount:
      `${configObj?.currencySymbol || ""} ${formattedAmount || "0.00"}` || "-",
  };

  const handleSnackbarClose = (
    _event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const openPopUp = useCallback((message: string, type: popupType) => {
    setPopupType(type);
    setGenericMessage(message);
    setOpenGenericModal(true);
  }, []);

  const handleChange = (
    event: React.MouseEvent<HTMLElement>,
    newAlignment: string
  ) => {
    setToggleBtn(newAlignment);
  };

  const updateQuantity = async (
    id: string,
    newQuantity: number,
    type: string
  ) => {};

  const handleCheckout = async (status?: string) => {
    const handleCreateLoadout = async () => {
      let isTimeoutTriggered = false;
      try {
        setLoading(true);
        let apiResponse: any = null;

        status = status ? status : "IN-TRANSIT";
        const requestBody = await getValidateRequestBody(
          cart,
          status,
          configObj?.loadoutConfigs?.isStockServiceEnabled,
          configObj?.newStatusUpdateConfig,
          loadNumber ?? "",
          configObj?.loadoutConfigs?.enableDeliveryDateAssignment ||
            configObj?.loadoutConfigs?.dateFilter === "deliveryDate"
            ? "deliveryDate"
            : "creationTime",
          configObj?.loadoutConfigs?.gapBetweenCreationAndDeliveryDate
        );

        const timeoutInSeconds =
          configObj?.loadoutConfigs?.Preseller
            ?.LoadoutRequestTimeoutInSeconds ?? 300;
        const timeoutInMs = timeoutInSeconds * 1000;

        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => {
            isTimeoutTriggered = true;
            reject(new Error("Request timed out"));
          }, timeoutInMs);
        });

        // Race the API call against the timeout
        apiResponse = await Promise.race([
          status === "IN-DRAFT" && loadNumber
            ? updateLoadout(requestBody)
            : status === "IN-DRAFT" && !loadNumber
            ? createLoadout(requestBody)
            : createLoadoutOrchestrator(requestBody),
          timeoutPromise,
        ]);

        if (apiResponse.status !== 200 && apiResponse.status !== 201) {
          throw new Error("API request failed");
        }

        setLoadoutNumber(
          apiResponse?.data?.loadNumber ??
            apiResponse?.data?.stepResponses?.LoadoutStep?.Result ??
            apiResponse?.data?.stepResponses?.CreateLoadoutStep?.Result
              ?.loadNumber
        );

        if (status === "IN-TRANSIT") {
          if (cart?.loadoutConfig?.loadslipMultipleFilters?.length > 0) {
            setShowLoadslipPopup(true);
          } else {
            setShowPopup(true);
          }
          if (isTimeoutTriggered) {
            setOpenGenericModal(false); // Dismiss timeout popup on success (shouldn't happen, but kept for consistency)
          }
        } else if (status === "IN-DRAFT") {
          dispatch({ type: "RESET_PRESELLER_LOADOUT" });
          navigate("/dashboard/delivery/loadouts");
        }
      } catch (error) {
        if (isTimeoutTriggered) {
          openPopUp(
            "The request is taking longer than expected and is still being processed in the background.",
            "Error"
          );
        } else {
          console.error(error);

          // Handle createLoadoutOrchestrator specific error responses
          if (
            status !== "IN-DRAFT" &&
            (error as any)?.response?.data?.stepResponses
          ) {
            const stepResponses = (error as any).response.data.stepResponses;
            let errorMessage = "Something went wrong while creating loadout";

            // Find the first failed step and get its error message
            for (const [stepName, stepResponse] of Object.entries(
              stepResponses
            )) {
              const typedStepResponse = stepResponse as any;
              if (typedStepResponse.Status === "Failed") {
                const errorCode = typedStepResponse.ErrorCode;
                const stepErrorKey = `${stepName}-ERROR${errorCode}`;

                if ((errorCodeToMessageMap as any)[stepErrorKey]) {
                  errorMessage = (errorCodeToMessageMap as any)[stepErrorKey];
                } else {
                  // If not found in errorCodeToMessageMap, use default message
                  errorMessage = "Something went wrong while creating loadout";
                }
                break;
              }
            }

            openPopUp(errorMessage, "Error");
          } else {
            // For createLoadout and updateLoadout APIs, show generic message
            openPopUp("Something went wrong while creating loadout", "Error");
          }
        }
      } finally {
        setLoading(false);
      }
    };
    logLoadTrace(
      handleCreateLoadout,
      "delivery_loadout_preseller_creation_time"
    );
  };

  const resetCart = () => {};

  const fetchRequiredConfigs = useCallback(
    async (loadoutConfig: any) => {
      const clientConfig = await getMetaDataConfig("clientconfig");
      const newStatusUpdateConfig = await fetchLoadoutConfigs(
        "newStatusUpdateConfig"
      );
      const customerConfig = clientConfig.find(
        (config: any) => config.domainType === "customer_configuration"
      );
      const catalogueConfig = clientConfig.find(
        (config: any) => config.domainType === "catalogue_configuration"
      );

      const currencySymbol = customerConfig.domainValues.find(
        (config: any) => config.name === "currency"
      );
      const ptrLabel = catalogueConfig.domainValues.find(
        (config: any) => config.name === "ptrLabel"
      );
      const mrpLabel = catalogueConfig.domainValues.find(
        (config: any) => config.name === "mrpLabel"
      );

      setFiltersLabel(
        loadoutConfig?.loadslipMultipleFilters?.map(
          (config: any) => config.label
        )
      );
      setConfigObj({
        currencySymbol: currencySymbol?.value ?? "₹",
        packUnitLabel: uomLabels.packUnitLabel,
        caseUnitLabel: uomLabels.caseUnitLabel,
        ptrLabel: ptrLabel?.value ?? "PTR",
        mrpLabel: mrpLabel?.value ?? "MRP",
        otherUnitLabel: uomLabels.otherUnitLabel,
        loadoutConfigs: loadoutConfig,
        newStatusUpdateConfig,
      });
    },
    [uomLabels]
  );

  // Keyboard shortcut handler
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for Ctrl+S (Windows/Linux) or Cmd+S (Mac)
      if ((event.ctrlKey || event.metaKey) && event.key === "s") {
        event.preventDefault(); // Prevent browser's default save action

        // Only trigger if not currently loading and not in view mode
        if (!loading && !viewLoadout) {
          handleCheckout();
        } else if (!loading && viewLoadout) {
          printLoadslip(
            loadoutNumber ?? "",
            cart,
            setSnackbarOpen,
            setIsDownloading,
            isDownloading,
            openPopUp,
            setSnackbarMessage,
            cart?.loadoutConfig?.loadSlipConfigs,
            selectedCheckboxes.map(
              (idx) => cart?.loadoutConfig?.loadslipMultipleFilters?.[idx]?.key
            )
          );
        }
      }
    };

    // Add event listener
    document.addEventListener("keydown", handleKeyDown);

    // Cleanup event listener on component unmount
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [loading, viewLoadout, handleCheckout, printLoadslip]);

  useEffect(() => {
    const uniqueOutlets = new Set(
      cart?.cartData?.salesData?.map((row: loadoutsSalesData) => row.outletCode)
    );
    setTotalUniqueOutlets(uniqueOutlets.size);
  }, [cart?.cartData?.salesData]);

  useEffect(() => {
    const getAndSetData = async () => {
      setLoading(true);
      try {
        const res = await getDataFromLocalDb("Cart");
        const savedCart = await JSON.parse(
          JSON.stringify(res?.data?.[0]?.data ?? {})
        );
        if ((savedCart?.cartData?.salesData ?? [])?.length) {
          dispatch({ type: "SET_PRESELLER_LOADOUT", payload: savedCart });
        } else {
          if (loadNumber) {
            navigate(`/dashboard/delivery/loadouts/preseller/${loadNumber}`);
          } else {
            navigate("/dashboard/delivery/loadouts/preseller");
          }
        }
        setLoading(false);
      } catch (error) {
        openPopUp("Something went wrong", "Error");
        setLoading(false);
        console.error(error);
      }
    };

    logLoadTrace(
      getAndSetData,
      "delivery_loadout_preseller_review_page_load_time"
    );
  }, []);

  useEffect(() => {
    if (cart?.loadoutConfig) {
      fetchRequiredConfigs(cart.loadoutConfig);
    }
  }, [cart?.loadoutConfig]);

  return (
    <div className="pre_seller_checkout_container">
      <GenericPopUp
        message={genericMessage}
        type={popupType}
        openGenericModal={openGenericModal}
        setOpenGenericModal={setOpenGenericModal}
      />
      {showPopup && (
        <CustomGenericModal
          type="success"
          message="The loadout has been created"
          modalOpen={showPopup}
          leftButtonText="Print Loadslip"
          onLeftButtonClick={() => {
            printLoadslip(
              loadoutNumber ?? "",
              cart,
              setSnackbarOpen,
              setIsDownloading,
              isDownloading,
              openPopUp,
              setSnackbarMessage,
              cart?.loadoutConfig?.loadSlipConfig,
              selectedCheckboxes.map(
                (idx) =>
                  cart?.loadoutConfig?.loadslipMultipleFilters?.[idx]?.key
              )
            );
          }}
          onClose={() => {
            setLoading(true);
            dispatch({ type: "RESET_PRESELLER_LOADOUT" });
            navigate("/dashboard/delivery/loadouts");
          }}
        />
      )}
      {showLoadslipPopup && (
        <CustomGenericModal
          type=""
          message={filtersLabel}
          modalOpen={showLoadslipPopup}
          onClose={() => {
            setShowLoadslipPopup(false);
            dispatch({ type: "RESET_PRESELLER_LOADOUT" });
            navigate("/dashboard/delivery/loadouts");
          }}
          errorMessages={false}
          addCheckBox={true}
          selectedCheckboxes={selectedCheckboxes}
          errorMessageText={errorMessageText}
          setSelectedCheckboxes={(selectedCheckboxes: number[]) => {
            setSelectedCheckboxes(selectedCheckboxes);
            setErrorMessageText("");
          }}
          headerText="The loadout has been created"
          subHeaderText="Select the Loadslip format you want to print"
          headerIcon={{
            headerIconType: "success",
            width: "30px",
          }}
          leftButtonText="Print"
          onLeftButtonClick={() => {
            if (selectedCheckboxes.length === 0) {
              setErrorMessageText(
                "Please select at least one format to download the Loadout Slip."
              );
              return;
            } else {
              setErrorMessageText("");
            }
            printLoadslip(
              loadoutNumber ?? "",
              cart,
              setSnackbarOpen,
              setIsDownloading,
              isDownloading,
              openPopUp,
              setSnackbarMessage,
              cart?.loadoutConfig?.loadSlipConfigs,
              selectedCheckboxes.map(
                (idx) =>
                  cart?.loadoutConfig?.loadslipMultipleFilters?.[idx]?.key
              )
            );
          }}
        />
      )}
      <Snackbar
        open={snackbarOpen}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="info"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <ConfirmationPopUp
        message={"Are you sure you want to reset the cart?"}
        openConfirmModal={confirmPopupState}
        successMethod={() => {
          resetCart();
        }}
        setOpenConfirmModal={setConfirmPopupState}
      />
      {loading ? (
        <Loader />
      ) : (
        <div className="pre_seller_checkout_innercontainer">
          <div
            style={{ height: displaySummary ? "" : "100%" }}
            className="pre_seller_checkout_grid_container"
          >
            <div
              style={{
                height: "100%",
                width: "61%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div>
                <div
                  style={{ marginBottom: toggleBtn === "sku" ? "" : "0" }}
                  className="pre_seller_checkout_add_reset_total_product"
                >
                  <Box
                    sx={{
                      backgroundColor: "#F0F0F0",
                      padding: "5px",
                      borderRadius: "8px",
                      height: "40px",
                    }}
                  >
                    <CustomToggleButtonGroup
                      value={toggleBtn}
                      exclusive
                      onChange={handleChange}
                      aria-label="Platform"
                    >
                      <ToggleButton
                        value="sku"
                        sx={{ letterSpacing: 0, fontSize: "0.95rem" }}
                      >
                        SKU Details (
                        {Object.keys(cart?.cartData?.product ?? {}).length})
                      </ToggleButton>
                      <ToggleButton
                        value="order"
                        sx={{ letterSpacing: 0, fontSize: "0.95rem" }}
                      >
                        {cart?.switchState
                          ? "Invoice Details"
                          : "Order Details"}{" "}
                        ({cart?.cartData?.salesData?.length})
                      </ToggleButton>
                    </CustomToggleButtonGroup>
                  </Box>
                  {!cart?.preseller && (
                    <div style={{ display: "flex", gap: "8px" }}>
                      <Button
                        color="primary"
                        style={{
                          border: "#2b82a1 solid 1px",
                          backgroundColor: "#ffffff",
                          fontSize: "11px",
                          fontWeight: 600,
                          textTransform: "none",
                        }}
                        onClick={() => setConfirmPopupState(true)}
                      >
                        <div
                          style={{
                            height: "24px",
                            width: "19px",
                            paddingRight: "4px",
                          }}
                        >
                          <RenderSvg name={"backIcon"} />
                        </div>
                        Reset Changes
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        style={{
                          fontSize: "11px",
                          padding: "0 11px",
                          fontWeight: 600,
                          textTransform: "none",
                        }}
                        sx={{
                          ":hover": {
                            boxShadow: "none",
                            background: "primary",
                          },
                          boxShadow: "none",
                        }}
                        onClick={() => {
                          navigate(-1);
                        }}
                      >
                        <LocalShippingIcon
                          style={{
                            height: "22px",
                            width: "14px",
                            marginRight: "4px",
                          }}
                        />
                        Add More
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              {toggleBtn === "sku" ? (
                <div
                  style={{
                    overflow: "auto",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {(Object.values(cart?.cartData?.product ?? {}) as Product[])
                    .sort(
                      (a, b) => (b.caseQuantity || 0) - (a.caseQuantity || 0)
                    )
                    .map((product: Product, index: number) => {
                      return (
                        <CartProductCard
                          key={index}
                          product={product}
                          updateQuantity={updateQuantity}
                          configObj={configObj}
                          preseller={cart?.preseller ?? false}
                          batchIdConfiguration={
                            cart?.loadoutConfig?.batchIdConfiguration ?? false
                          }
                        />
                      );
                    })}
                </div>
              ) : (
                <div
                  style={{
                    height: "100%",
                    overflow: "auto",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <CustomDataGrid
                    rows={cart?.cartData?.salesData ?? []}
                    columnsMetaData={
                      cart?.switchState
                        ? presellerReviewColumnsMetaData
                        : {
                            ...presellerReviewColumnsMetaData,
                            columns:
                              presellerReviewColumnsMetaData.columns.filter(
                                (column) => column.headerName !== "Invoice No."
                              ),
                          }
                    }
                    tableWidth={0.47}
                  />
                </div>
              )}
            </div>
            <div
              style={{
                overflowY: "auto",
              }}
              className="pre_seller_checkout_loadout_details_grid_container"
            >
              <div className="pre_seller_loadout_details_checkout">
                <div className="pre_seller_sub_header">
                  <div className="pre_seller_confirmed_order_icon">
                    <RenderSvg name="customerIcon" />
                  </div>
                  <Typography
                    sx={{ fontSize: "16px", color: "#227c9d", fontWeight: 600 }}
                  >
                    Loadout Details
                  </Typography>
                </div>

                <Divider sx={{ height: "14px" }} color="#f1f1f1" />
                {configObj?.loadoutConfigs?.summaryKeys.map(
                  (
                    summaryKey: {
                      label: string;
                      key: keyof typeof summaryValues;
                    },
                    index: number
                  ) => {
                    return (
                      <div
                        className="pre_seller_label_value_container"
                        key={index}
                      >
                        <Typography
                          sx={{
                            fontSize: "12px",
                            lineHeight: 2.3,
                            minWidth: "25%",
                          }}
                        >
                          {summaryKey?.label}
                        </Typography>
                        <Typography
                          sx={{ fontSize: "12px", lineHeight: 2.3 }}
                          className="pre_seller_label_values"
                        >
                          {summaryValues[summaryKey?.key]}
                        </Typography>
                      </div>
                    );
                  }
                )}
              </div>
            </div>
          </div>

          <div className="pre_seller_Checkout_cartSummaryFooter_container_wrapper">
            <div
              className="pre_seller_Checkout_cartSummaryFooter_container"
              style={{ height: "auto" }}
            >
              <CartSummaryFooter
                totalAmountAndQtyInfo={{
                  numberOfProducts: Object.keys(cart?.cartData?.product ?? {})
                    .length,
                  totalCaseQuantity: cart?.totals?.totalCase,
                  totalPieceQuantity: cart?.totals?.totalPiece,
                  totalOtherUnitQuantity: cart?.totals?.totalOther,
                  totalAmount: cart?.totals?.totalAmount,
                }}
                cartProducts={cart?.cartData?.product}
                onButtonClick={
                  viewLoadout
                    ? () =>
                        printLoadslip(
                          loadNumber ?? "",
                          cart,
                          setSnackbarOpen,
                          setIsDownloading,
                          isDownloading,
                          openPopUp,
                          setSnackbarMessage,
                          cart?.loadoutConfig?.loadSlipConfigs,
                          selectedCheckboxes.map(
                            (idx) =>
                              cart?.loadoutConfig?.loadslipMultipleFilters?.[
                                idx
                              ]?.key
                          )
                        )
                    : handleCheckout
                }
                buttonText={viewLoadout ? "" : "Create Loadout"}
                loadout={true}
                disabled={loading}
                reviewLoadout={true}
                displaySaveButton={!viewLoadout}
                hideShortcutKeys={true}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Checkout;
